import type { WithShopId, AddToCartItem, VirtualSkuInfo } from './types';
import API_ERROR from '@shopee/api-error-codes';

export function zip(entries: Array<any>) {
  if (!entries) return entries;
  const returnValue: any = {};
  entries.forEach(([key, value]) => {
    returnValue[key] = value;
  });
  return returnValue;
}

/**
 * Normalize data before submit.
 * - shopId => shopid, etc.
 * - remove keys that have empty value.
 * @param item
 * @returns {*|*}
 */
export function processItemBeforeSubmit(item: {
  shopId: number;
  modelId?: number | null;
  itemId: number;
  virtualSkuInfo?: VirtualSkuInfo | null;
}) {
  const { shopId, itemId, modelId, virtualSkuInfo, ...rest } = item;
  const data = {
    ...rest,
    shopid: shopId,
    itemid: itemId,
    modelid: typeof modelId === 'number' && modelId > 0 ? modelId : void 0,
    ...(virtualSkuInfo
      ? {
          virtual_sku_info: {
            itemid: virtualSkuInfo.vItemId,
            shopid: virtualSkuInfo.vShopId,
            modelid: virtualSkuInfo.vModelId,
          },
        }
      : null),
  };
  return zip(
    Object.entries(data).filter(
      ([_, value]) => value !== null && value !== void 0
    )
  );
}

/**
 * Group item by `shopid`.
 * Can check test cases in the folder.
 * @param items
 * @returns {Array<*>}
 */
export function groupItemsByShopId<T>(
  items: Array<WithShopId & T>
): Array<{ shopid: number | string; items: Array<T> }> {
  const groupedItemMap = items.reduce(
    (
      map: Record<
        number | string,
        { shopid: number | string; items: Array<T> }
      >,
      item
    ) => {
      const shopId = item.shopid || '';
      const shopGroup = map[shopId];
      if (!shopGroup) {
        map[shopId] = {
          shopid: shopId,
          items: [item],
        };
      } else {
        shopGroup.items.push(item);
      }
      return map;
    },
    {}
  );

  return Object.values(groupedItemMap);
}

export function getResetGroupFromItems(items: Array<AddToCartItem>) {
  return items.reduce(
    (
      arr: Array<{
        item_group_id: AddToCartItem['item_group_id'];
        shop_id: AddToCartItem['shopId'];
      }>,
      item
    ) => {
      if (
        arr.find(
          ({ item_group_id, shop_id }) =>
            shop_id === item.shopId && item_group_id === item.item_group_id
        )
      ) {
        return arr;
      }
      return [
        ...arr,
        { item_group_id: item.item_group_id, shop_id: item.shopId },
      ];
    },
    []
  );
}

export function shouldUsePopupWhenAddToCartError(error: number) {
  return error === API_ERROR.CART_BFF_TOTAL_QUANTITY_EXCEEDED;
}
